import { Component, ElementRef, EventEmitter, Input, Output, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { NgModel } from '@angular/forms';
import {
    FtCodeMirrorComponent
} from 'apps/account/src/account-shared/code-mirror/ft-code-mirror.component';
import { EditorField } from '../../../models/editor-field';
import { EditorToken } from '../../../models/editor-token';
import { RichTextEditorComponent, RichTextFormat } from '../rich-text-editor/rich-text-editor.component';


@Component({
    selector: 'ft-text-html-editor',
    templateUrl: 'text-html-editor.component.html',
    styleUrls: ['./text-html-editor.component.scss'],
    standalone: false
})
export class TextHtmlEditorComponent {
    @ViewChildren('input', { read: NgModel }) inputs: QueryList<NgModel>;
    /** The shared toolbar's link-popover input. Focused here because the popover is rendered here. */
    @ViewChild('linkInput') linkInputRef: ElementRef<HTMLInputElement>;
    @Input() plain = false;
    @Input() trimClipboardFormat = false;
    @Input() fields: EditorField[];
    @Input() tokens: EditorToken[];
    @Input() required: boolean;
    @Input() visible = true;
    @Input() email: boolean;
    @Input() maxlength: number;
    @Input() minlength: number;
    @Input() pattern: string;
    @Input() placeholder = '';
    @Input() model: any;
    @Input() currentIndex: number;
    @Input() totalEntries: number;
    @Output() modelChange = new EventEmitter<any>();

    isSourceCodeVisible = false;
    selectedToken = null;
    activeEditorIndex: number | null = null;
    richTextEditors: RichTextEditorComponent[] = [];
    /** The html source-view editors, keyed by field index (was Monaco, now CodeMirror 6). */
    codeEditors: FtCodeMirrorComponent[] = [];
    @Input() visibleFormats: string[] = ['*'];

    trimUnbreakableSpaceOnPaste(e: ClipboardEvent, field: EditorField) {
        if (field) {
            this.model[field.name] = this.model[field.name].replace(/&(nbsp);/g, ' ');
        } else {
            this.model = this.model.replace(/&(nbsp);/g, ' ');
        }
    }

    constructor() {
    }

    onModelChanged(model: string, field?: EditorField) {
        if (field) {
            this.model[field.name] = model;
        } else {
            this.model = model;
        }

        this.modelChange.next(this.model);
    }

    onBlur(i) {
        if (i === this.activeEditorIndex && this.fields) {
            this.activeEditorIndex = null;
        }
    }

    onFocus(index: number) {
        this.activeEditorIndex = index;
    }

    onRichTextEditorCreated(editor: RichTextEditorComponent, index: number) {
        const indexToFocusInArray = !isNaN(this.currentIndex) ? this.currentIndex : index;
        this.richTextEditors[index] = editor;

        if (indexToFocusInArray === 0) {
            this.focusEditor(editor);
        }
    }

    focusEditor(editor: RichTextEditorComponent) {
        setTimeout(() => {
            editor.focus();
        }, 200);
    }

    codeEditorCreated(editor: FtCodeMirrorComponent, index: number) {
        this.codeEditors[index] = editor;
    }

    changeFormat() {
        this.isSourceCodeVisible = !this.isSourceCodeVisible;
        // Both halves are `*ngIf`'d on this flag, so every handle the toggle just destroyed is now a
        // dead component. The shared toolbar reads `activeEditor` on each check — leaving stale
        // entries here would let it issue an execCommand against a detached editable, which would
        // land on whatever the document selection happens to be (the source view's own text).
        this.richTextEditors = [];
        this.codeEditors = [];
    }

    // ---- The shared toolbar, which acts on whichever editor in the group is focused ----

    get activeEditor(): RichTextEditorComponent | null {
        return this.activeEditorIndex === null ? null : this.richTextEditors[this.activeEditorIndex] ?? null;
    }

    /** Empty when nothing is focused, which is also when the toolbar is dimmed and click-through. */
    get activeFormats(): Partial<Record<RichTextFormat, boolean>> {
        return this.activeEditor?.activeFormats ?? {};
    }

    get linkEditorOpen(): boolean {
        return !!this.activeEditor?.linkEditorOpen;
    }

    get linkDraft(): string {
        return this.activeEditor?.linkDraft ?? '';
    }

    applyFormat(format: RichTextFormat) {
        const editor = this.activeEditor;
        if (!editor) {
            return;
        }
        editor.applyFormat(format);
        // `link` only opens the popover; focusing its input is this component's job now that the
        // popover is rendered here. Deferred so the `*ngIf` has rendered the input first.
        if (format === 'link') {
            setTimeout(() => this.linkInputRef?.nativeElement?.focus());
        }
    }

    commitLink(url: string) {
        this.activeEditor?.commitLink(url);
    }

    removeLink() {
        this.activeEditor?.removeLink();
    }

    closeLinkEditor() {
        this.activeEditor?.closeLinkEditor();
    }

    onTokenSelected(token: string) {
        if (this.isSourceCodeVisible) {
            this.insertTokenInCodeEditor(token);
        } else {
            this.insertTokenInRichText(token);
        }

        setTimeout(() => {
            this.selectedToken = null;
        });
    }


    insertTokenInRichText(token: string) {
        // Optional call rather than the old `quillEditors[activeEditorIndex].getSelection()`:
        // picking a token moves focus to the <ng-select>, and `onBlur` nulls activeEditorIndex
        // whenever `fields` is set, so the old form threw on exactly those surfaces. The editor
        // restores the caret from the range it saved while it was focused.
        this.richTextEditors[this.activeEditorIndex]?.insertText(token);
    }

    /**
     * Monaco built a `Range` from `getSelection()` and ran `executeEdits(…, forceMoveMarkers)`;
     * CodeMirror expresses the same thing as one transaction that replaces the current selection
     * and leaves the caret after the inserted text (`insertTextTransaction`).
     *
     * FTK-7509: this was always correct — what was missing is that `activeEditorIndex` was never
     * set while the source view was open, so it indexed `codeEditors[null]` and the optional call
     * quietly did nothing. `<ft-code-mirror (editorFocus)>` now supplies the index; see the comment
     * on that binding for why blur is deliberately left unwired.
     */
    insertTokenInCodeEditor(token: string) {
        if (token) {
            this.codeEditors[this.activeEditorIndex]?.insertText(token);
        }
    }

    isFormatVisible(format: string): boolean {
        return this.visibleFormats?.some(entry =>  entry === format || entry === '*');
    }
}

